/**
 * Pure update-status logic, deliberately free of React / Capacitor / config
 * imports so it can be unit-tested in isolation (the surrounding hook pulls in
 * the whole frontend chain). The hook in useAppUpdate.ts wires the runtime
 * values into this.
 */
export interface AppUpdateStatus {
  /** Running binary is below the platform's `minSupported` floor — hard block. */
  forceUpdate: boolean;
  /** A newer build than this binary is live in the store — soft prompt. */
  updateAvailable: boolean;
}

export const IDLE_UPDATE_STATUS: AppUpdateStatus = {
  forceUpdate: false,
  updateAvailable: false,
};

/**
 * Compare the running build against the platform floors. Precedence:
 * force-update (below `minSupported`) beats the soft "update available" nudge
 * (below `latest`), which beats idle. Fails open when the build is unknown.
 */
export function computeUpdateStatus({
  platform,
  build,
  minSupported,
  latest,
  playStoreAvailable,
}: {
  platform: string;
  build: number | null;
  minSupported?: number;
  latest?: number;
  playStoreAvailable: boolean;
}): AppUpdateStatus {
  if (build == null) return IDLE_UPDATE_STATUS;

  if (typeof minSupported === "number" && build < minSupported) {
    return { forceUpdate: true, updateAvailable: false };
  }

  // The soft prompt runs on iOS always; on Android only once the Play listing
  // is public (until then there is nowhere to send the user).
  const eligible =
    platform === "ios" || (platform === "android" && playStoreAvailable);
  const updateAvailable =
    eligible && typeof latest === "number" && build < latest;

  return { forceUpdate: false, updateAvailable };
}
